import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { AccountTypes, FlowTypes, urlMapper } from '@shared/models';
import { LoggerCodes } from '@shared/modules/logger/constants/logger.constants';
import { LoggerService } from '@shared/modules/logger/service/logger.service';
import { Go } from '@store/actions';
import * as fromRootStore from '@store/index';
import { selectMultiITTriageTransferFrom } from '@store/internal-transfers/selectors/multi-internal-transfer.selectors';
import { InternalTransferTriage } from '@store/reducers/internal-transfer/internal-transfer.reducer';
import { TriageState } from '@store/reducers/triage/triage.reducer';
import { selectInternalTransferTriage } from '@store/selectors/internal-transfer/internal-transfer.selectors';
import { selectPersonalDataLoaded } from '@store/selectors/personal-data/personal-data.selectors';
import { selectTriageState } from '@store/selectors/triage/triage.selectors';
import { CookieService } from 'ngx-cookie-service';
import { catchError, combineLatest, filter, Observable, of, switchMap, take } from 'rxjs';

@Injectable()
export class InitStateGuard {
  constructor(
    private globalStore: Store<fromRootStore.State>,
    private cookieService: CookieService,
    private readonly loggerService: LoggerService,
  ) {}

  canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    if (this.cookieService.get('routeGuard') === 'OFF') {
      return of(true);
    }

    // Skip triage validation for routes that populate their own state (e.g. additional signer flows)
    if (this.shouldSkipCheck(state)) {
      this.loggerService.info(
        {
          eventId: 'guard-skip-init-state',
          message: `InitStateGuard skipped for route: ${state.url}`,
        },
        LoggerCodes.GENERAL_SUCCESS,
      );
      return of(true);
    }

    // Check both triage state and personal data
    // Account registrations are loaded by ClientDataGuard
    return this.checkState().pipe(
      switchMap((canActivate) => {
        if (canActivate) {
          return of(true);
        } else {
          // Redirect to overview page based on current URL
          const overviewPage = overviewRedirect(state.url);
          this.globalStore.dispatch(Go({ path: [`/${overviewPage}`] }));
          return of(false);
        }
      }),
      catchError(() => of(false)),
    );
  }

  checkState(): Observable<boolean> {
    // Wait for personal data to be loaded by ClientDataGuard
    return this.globalStore.select(selectPersonalDataLoaded).pipe(
      filter((loaded) => loaded),
      take(1),
      switchMap(() =>
        combineLatest([
          this.globalStore.select(selectTriageState),
          this.globalStore.select(selectInternalTransferTriage),
          this.globalStore.select(selectMultiITTriageTransferFrom),
        ]).pipe(
          take(1),
          switchMap(([triageState, internalTransferTriageState, multiITTriageTransferFrom]) => {
            // Check if triage state is valid
            const hasValidTriageState = this.hasValidStateParams(
              triageState,
              internalTransferTriageState,
              multiITTriageTransferFrom,
            );

            if (!hasValidTriageState) {
              return of(false);
            }

            return of(true);
          }),
        ),
      ),
    );
  }

  hasValidStateParams(
    triageState: TriageState,
    internalTransferTriageState: InternalTransferTriage,
    multiITTriageTransferFrom: string,
  ): boolean {
    return (
      this.enumIncludes(FlowTypes, triageState.flowType) &&
      (triageState.flowType === FlowTypes.MULTI_INHERITANCE ||
        triageState.flowType === FlowTypes.INHERITED_IRA ||
        triageState.flowType === FlowTypes.DIVORCE ||
        this.enumIncludes(
          AccountTypes,
          triageState.accountTypeInheriting ||
            internalTransferTriageState.transferFrom ||
            multiITTriageTransferFrom,
        ))
    );
  }

  shouldSkipCheck(state: RouterStateSnapshot): boolean {
    let route: ActivatedRouteSnapshot | null = state.root;
    while (route) {
      if (route.data['skipInitStateCheck'] === true) {
        return true;
      }
      route = route.firstChild;
    }
    return false;
  }

  enumIncludes(e: Record<string, string | number>, value: string): boolean {
    // excluding empty string and non-string values
    const stringValues = Object.values(e).filter(
      (enumValue): enumValue is string => typeof enumValue === 'string' && enumValue !== '',
    );
    return stringValues.includes(value);
  }
}

export function overviewRedirect(url: string): string {
  let currentFlow: FlowTypes;

  switch (true) {
    case urlMapper.MINOR && url.includes(urlMapper.MINOR):
      currentFlow = FlowTypes.MINOR;
      break;
    case urlMapper.GIFTING && url.includes(urlMapper.GIFTING):
      currentFlow = FlowTypes.GIFTING;
      break;
    case urlMapper.INTERNAL_TRANSFER && url.includes(urlMapper.INTERNAL_TRANSFER):
      currentFlow = FlowTypes.INTERNAL_TRANSFER;
      break;
    case urlMapper.MULTI_INHERITANCE && url.includes(urlMapper.MULTI_INHERITANCE):
      currentFlow = FlowTypes.MULTI_INHERITANCE;
      break;
    case urlMapper.INHERITED_IRA && url.includes(urlMapper.INHERITED_IRA):
      currentFlow = FlowTypes.INHERITED_IRA;
      break;
    case urlMapper.DIVORCE && url.includes(urlMapper.DIVORCE):
      currentFlow = FlowTypes.DIVORCE;
      break;
    case urlMapper.INHERITANCE && url.includes(urlMapper.INHERITANCE):
    default:
      currentFlow = FlowTypes.INHERITANCE;
      break;
  }

  const overviewPage: { [key: string]: string } = {
    INHERITANCE: 'inheritance/overview',
    MULTI_INHERITANCE: 'inheritance/overview',
    MINOR: 'minor-transfer/overview',
    GIFTING: 'gifting/overview',
    INTERNAL_TRANSFER: 'internal-transfer/overview',
    INHERITED_IRA: 'inherited-ira/triage',
    DIVORCE: 'internal-transfer/triage',
  };

  return overviewPage[currentFlow];
}
